import {
  Box,
  Divider,
  Typography,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  chartColors,
  incomeLabels,
  demoLabels,
  ageLabels,
  nationalStats,
} from "../../constants";
import { nationalChartOptions } from "./nationalChartOptions";
import ChartDataLabels from "chartjs-plugin-datalabels";

export default function NationalStats() {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );

  const demoData = {
    labels: demoLabels,
    datasets: [
      {
        label: "",
        data: [
          nationalStats.PercentPopWithin.white,
          nationalStats.PercentPopWithin.black,
          nationalStats.PercentPopWithin.nativeAmerican,
          nationalStats.PercentPopWithin.asian,
          nationalStats.PercentPopWithin.pacificIslander,
          nationalStats.PercentPopWithin.other,
          nationalStats.PercentPopWithin.twoOrMore,
          nationalStats.PercentPopWithin.hispanic,
        ],
        borderColor: chartColors,
        backgroundColor: chartColors,
      },
    ],
  };

  const incomeData = {
    labels: incomeLabels,
    datasets: [
      {
        label: "",
        data: [
          nationalStats.PercentPopWithin.lowIncomeHouse,
          nationalStats.PercentPopWithin.medIncomeHouse,
          nationalStats.PercentPopWithin.highIncomeHouse,
        ],
        borderColor: chartColors,
        backgroundColor: chartColors,
      },
    ],
  };

  const ageData = {
    labels: ageLabels,
    datasets: [
      {
        label: "",
        data: [
          nationalStats.PercentPopWithin.kids,
          nationalStats.PercentPopWithin.adults,
          nationalStats.PercentPopWithin.seniors,
        ],
        borderColor: chartColors,
        backgroundColor: chartColors,
      },
    ],
  };

  return (
    <Box
      px={2}
      py={3}
      bgcolor="white"
      boxSizing="border-box"
    >
      <Typography
        sx={{ textAlign: "center" }}
        variant="h1"
        color="earth.main"
        gutterBottom
      >
        Welcome to ParkServe!
      </Typography>
      <Box
        sx={{
          display: "flex",
          width: "100%",
        }}
      >
        <Box sx={{ textAlign: "center", padding: "8px" }}>
          <Typography variant="h4">
            ParkServe is the country's most comprehensive parks database for
            over 15,000 urban cities and towns across the country
            ("census-designated urban areas"). Across these urban areas, more
            than 100 million people— including 28 million kids—don’t have a park
            within a 10-minute walk of home. How does your community stack up?
            Enter your city name in the search bar at the top of the page to get
            started!
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ marginTop: "25px" }} />

      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        sx={{ marginTop: "15px", marginBottom: "5px" }}
      >
        <Box display="flex" flexDirection="column" width="110px">
          <Typography variant="subtitle2" color="">
            Percent of Residents within a 10-Minute Walk
          </Typography>
          <Typography variant="h3" color="">
            {(
              (nationalStats.popWithin10minWalk / nationalStats.totalUSPop) *
              100
            ).toFixed(0)}{" "}
            %
          </Typography>
          <Typography variant="subtitle2" color="">
            Population Served
          </Typography>
          <Typography variant="h3" color="">
            {nationalStats.popWithin10minWalk.toLocaleString()}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" width="110px">
          <Typography variant="subtitle2" color="">
            Percent of Residents outside a 10-Minute Walk
          </Typography>
          <Typography variant="h3" color="">
            {(
              (nationalStats.popOutside10minWalk / nationalStats.totalUSPop) *
              100
            ).toFixed(0)}{" "}
            %
          </Typography>
          <Typography variant="subtitle2" color="">
            Total Population of ParkServe Cities
          </Typography>
          <Typography variant="h3" color="">
            {nationalStats.totalUSPop.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <Divider />
      <Typography
        variant="h5"
        color="grass.main"
        sx={{ marginTop: "10px", marginBottom: "10px" }}
      >
        Race/Ethnicity
      </Typography>
      <Typography variant="subtitle2" color="">
        Percentage of people within a 10-minute walk by race/ethnicity.
        *Excludes those that report Hispanic origin (which is captured
        separately from race by the U.S. Census).
      </Typography>
      <Box height="400px" width="100%">
        <Bar
          options={nationalChartOptions(nationalStats.TotalPopWithin, demoLabels)}
          plugins={[ChartDataLabels]}
          data={demoData}
        />
      </Box>
      <Divider />
      <Typography
        variant="h5"
        color="grass.main"
        sx={{ marginTop: "10px", marginBottom: "10px" }}
      >
        Age
      </Typography>
      <Typography variant="subtitle2" color="">
        Percentage of people within a 10-minute walk by age
      </Typography>
      <Box height="200px" width="100%">
        <Bar
          options={nationalChartOptions(nationalStats.TotalPopWithin, ageLabels)}
          plugins={[ChartDataLabels]}
          data={ageData}
        />
      </Box>
      <Divider />
      <Typography
        variant="h5"
        color="grass.main"
        sx={{ marginTop: "10px", marginBottom: "10px" }}
      >
        Income
      </Typography>
      <Typography variant="subtitle2" color="">
        Percentage or households within a 10-minute walk of a park by income.
        MAI = Median Area Income, which is the median income of the urban area.{" "}
      </Typography>
      <Box height="200px" width="100%">
        <Bar
          options={nationalChartOptions(nationalStats.TotalPopWithin, incomeLabels)}
          plugins={[ChartDataLabels]}
          data={incomeData}
        />
      </Box>
    </Box>
  );
}
